import { User } from '@xver/@types/user';
import { Avatar } from '@xver/components/Atoms';
import DialogBox from '@xver/components/Atoms/DialogBox/DialogBox';
import { useTranslation } from 'react-i18next';
import AccountStatusBadge from './AccountStatusBadge';
import EmailVerificationBadge from './EmailVerificationBadge';

interface ProfileSummaryCardProps {
    user: User;
}

export default function ProfileSummaryCard({ user }: ProfileSummaryCardProps) {
    const { t } = useTranslation('profile');

    return (
        <DialogBox
            label={t('summary.title')}
            description={t('summary.description')}
            classNames="p-4"
        >
            <div className="w-full flex flex-col md:flex-row gap-6 items-start">
                <div className="w-full h-full flex flex-row gap-3 items-center">
                    <Avatar
                        value={`${user.id}:${user.email}:${user.information.firstName}:${user.information.lastName}`}
                        size="50"
                    />
                    <div className="flex flex-col justify-center">
                        <span className="text-lg font-bold">
                            {user.information.firstName || user.email.split('@')[0]}
                            {user.information.lastName ? ` ${user.information.lastName}` : ''}
                        </span>
                        <span className="text-sm leading-5 text-fg/68">{user.email}</span>
                    </div>
                </div>
                <div className="w-full h-full grid grid-cols-2 items-center md:grid-cols-4 gap-4">
                    <SummaryItem
                        label={t('summary.credit')}
                        value={`${user.credit.toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                        })} ฿`}
                    />
                    <SummaryItem
                        label={t('summary.status')}
                        value={<AccountStatusBadge status={user.status} />}
                    />
                    <SummaryItem
                        label={t('summary.kycLevel')}
                        value={t(`kyc.level.${user.kyc.level}`)}
                    />
                    <SummaryItem
                        label={t('summary.emailVerification')}
                        value={<EmailVerificationBadge level={user.verifyEmailAddress} />}
                    />
                </div>
            </div>
        </DialogBox>
    );
}

function SummaryItem({ label, value }: { label: string; value: React.ReactNode }) {
    return (
        <div className="flex flex-col gap-1">
            <span className="text-sm text-fg/68">{label}</span>
            <span className="text-base">{value}</span>
        </div>
    );
}
